/* ═══════════════════════════════════════════════════════════════
   SORISSE — sorisse.js
   No dependencies. One rAF loop drives every scroll-linked effect.
   ═══════════════════════════════════════════════════════════════ */
(() => {
'use strict';

const $  = (s, c = document) => c.querySelector(s);
const $$ = (s, c = document) => [...c.querySelectorAll(s)];
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const lerp  = (a, b, t) => a + (b - a) * t;

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
const FINE    = matchMedia('(hover: hover) and (pointer: fine)').matches;
const html    = document.documentElement;

/* ─── 1 · PRELOADER ──────────────────────────────────────────── */
{
  const pre = $('#pre');
  const reveal = () => {
    html.classList.add('ready');
    pre && pre.classList.add('done');
    setTimeout(() => pre && pre.remove(), 900);
  };
  if (REDUCED) { reveal(); }
  else {
    let done = false;
    const go = () => { if (!done) { done = true; reveal(); } };
    // whichever comes first: fonts + load, or a hard 1.6s ceiling
    Promise.race([
      Promise.all([
        document.fonts ? document.fonts.ready : Promise.resolve(),
        new Promise(r => window.addEventListener('load', r, { once: true }))
      ]),
      new Promise(r => setTimeout(r, 1600))
    ]).then(() => setTimeout(go, 220));
    setTimeout(go, 2600);
  }
}

/* ─── 2 · SMOOTH SCROLL ──────────────────────────────────────── */
const scroller = {
  enabled: !REDUCED && FINE,
  target: window.scrollY,
  current: window.scrollY,
  max: 0,
  measure() { this.max = Math.max(0, document.body.scrollHeight - innerHeight); },
  lock(on) {
    html.style.overflow = on ? 'hidden' : '';
    this.paused = on;
    if (!on) { this.current = this.target = window.scrollY; }
  },
  to(y) {
    if (!this.enabled || this.paused) { window.scrollTo({ top: y, behavior: REDUCED ? 'auto' : 'smooth' }); return; }
    this.measure();
    this.target = clamp(y, 0, this.max);
  },
  tick() {
    if (!this.enabled || this.paused) return;
    const d = this.target - this.current;
    if (Math.abs(d) > 0.09) {
      this.current += d * 0.115;
      window.scrollTo(0, this.current);
    } else {
      this.current = this.target = window.scrollY;
    }
  }
};
scroller.measure();
addEventListener('resize', () => scroller.measure(), { passive: true });

if (scroller.enabled) {
  addEventListener('wheel', e => {
    if (scroller.paused || e.ctrlKey) return;
    e.preventDefault();
    scroller.measure();
    const unit = e.deltaMode === 1 ? 22 : e.deltaMode === 2 ? innerHeight : 1;
    scroller.target = clamp(scroller.target + e.deltaY * unit, 0, scroller.max);
  }, { passive: false });

  addEventListener('scroll', () => {
    // external scroll (scrollbar drag, keyboard, find-in-page) — resync
    if (Math.abs(window.scrollY - scroller.current) > 3) {
      scroller.current = scroller.target = window.scrollY;
    }
  }, { passive: true });
}

/* anchors */
$$('a[href^="#"]').forEach(a => {
  a.addEventListener('click', e => {
    const id = a.getAttribute('href');
    if (id === '#' || id.length < 2) return;
    const el = $(id);
    if (!el) return;
    e.preventDefault();
    closeMenu(); closeCart();
    const top = el.getBoundingClientRect().top + window.scrollY - ($('#nav').offsetHeight - 12);
    scroller.to(top);
  });
});

/* ─── 3 · REVEALS ────────────────────────────────────────────── */
{
  const targets = $$('.rv, .display, .hero__h, .facts li, .step');
  if (REDUCED || !('IntersectionObserver' in window)) {
    targets.forEach(el => el.classList.add('in'));
  } else {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(en => {
        if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });
    targets.forEach((el, i) => {
      // stagger siblings inside a shared parent
      const sibs = el.parentElement ? [...el.parentElement.children].filter(c => targets.includes(c)) : [];
      if (sibs.length > 1) el.style.setProperty('--d', sibs.indexOf(el));
      io.observe(el);
    });
  }
}

/* ─── 4 · NAV · MENU · CART ──────────────────────────────────── */
const nav = $('#nav'), menu = $('#menu'), burger = $('#burger');
const cart = $('#cart'), scrim = $('#scrim');

function closeMenu() {
  if (!menu || menu.hidden) return;
  menu.classList.remove('on'); burger.classList.remove('on');
  burger.setAttribute('aria-expanded', 'false');
  scroller.lock(false);
  setTimeout(() => { menu.hidden = true; }, 700);
}
function openMenu() {
  menu.hidden = false;
  void menu.offsetWidth;            // reflow, so the transition has a start value
  menu.classList.add('on');
  burger.classList.add('on'); burger.setAttribute('aria-expanded', 'true');
  scroller.lock(true);
}
burger && burger.addEventListener('click', () => menu.hidden ? openMenu() : closeMenu());

function openCart() {
  cart.hidden = false; scrim.hidden = false;
  void cart.offsetWidth;            // reflow beats rAF here — rAF is throttled in hidden tabs
  cart.classList.add('on'); scrim.classList.add('on');
  cart.setAttribute('aria-hidden', 'false');
  $('#bagBtn').setAttribute('aria-expanded', 'true');
  scroller.lock(true);
  $('#cartClose').focus();
}
window.openCart = openCart;

function closeCart() {
  if (!cart || cart.hidden) return;
  cart.classList.remove('on'); scrim.classList.remove('on');
  cart.setAttribute('aria-hidden', 'true');
  $('#bagBtn').setAttribute('aria-expanded', 'false');
  scroller.lock(false);
  setTimeout(() => { cart.hidden = true; scrim.hidden = true; }, 650);
}
$('#bagBtn') && $('#bagBtn').addEventListener('click', openCart);
$('#cartClose') && $('#cartClose').addEventListener('click', closeCart);
scrim && scrim.addEventListener('click', closeCart);
addEventListener('keydown', e => { if (e.key === 'Escape') { closeCart(); closeMenu(); } });

/* ─── 5 · TECHNOLOGY TABS ────────────────────────────────────── */
{
  const tabs   = $$('.wave');
  const shots  = $$('.tech__shot img');
  const pans   = $$('.pan');
  const glow   = $('#techGlow');
  const tech   = $('.tech');
  const depth  = $('.depth');
  const line   = $('#waveLine');

  const CFG = {
    red:    { beam: 'var(--red)',    reach: 2, freq: 1.0  },
    violet: { beam: 'var(--violet)', reach: 2, freq: 0.72 },
    yellow: { beam: 'var(--amber)',  reach: 1, freq: 0.9  }
  };

  // one long wave; scaleX changes the apparent wavelength
  if (line) {
    let d = 'M0 75', x = 0, up = true;
    while (x < 1400) { const nx = x + 26; d += ` Q ${x + 13} ${up ? 26 : 124} ${nx} 75`; x = nx; up = !up; }
    line.setAttribute('d', d);
    line.style.transition = 'transform 1s cubic-bezier(.22,.61,.36,1)';
    line.style.transformOrigin = '0px 75px';
  }

  let idx = 0, timer = null;

  function show(i, manual) {
    idx = (i + tabs.length) % tabs.length;
    const key = tabs[idx].dataset.wave, cfg = CFG[key];

    tabs.forEach((t, n) => {
      const on = n === idx;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      // restart the sweep bar
      const bar = $('.wave__bar', t); if (bar) { bar.style.animation = 'none'; void bar.offsetWidth; bar.style.animation = ''; }
    });
    shots.forEach(s => s.classList.toggle('is-on', s.dataset.shot === key));
    pans.forEach((p, n) => { p.hidden = n !== idx; if (n === idx) { p.style.animation = 'none'; void p.offsetWidth; p.style.animation = ''; } });

    tech.style.setProperty('--beam', cfg.beam);
    if (glow) glow.style.background = `radial-gradient(circle, color-mix(in srgb, ${cfg.beam} 20%, transparent) 0%, transparent 64%)`;
    if (depth) depth.dataset.reach = cfg.reach;
    if (line) line.style.transform = `scaleX(${cfg.freq})`;

    if (manual) restart();
  }

  function restart() {
    clearInterval(timer);
    if (REDUCED) return;
    timer = setInterval(() => show(idx + 1), 7000);
  }

  tabs.forEach((t, i) => {
    t.addEventListener('click', () => show(i, true));
    t.addEventListener('keydown', e => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); show(idx + 1, true); tabs[idx].focus(); }
      if (e.key === 'ArrowUp'   || e.key === 'ArrowLeft')  { e.preventDefault(); show(idx - 1, true); tabs[idx].focus(); }
    });
  });

  if (tabs.length) {
    show(0);
    // only run the carousel while the section is on screen
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(es => {
        es.forEach(en => en.isIntersecting ? restart() : clearInterval(timer));
      }, { threshold: 0.25 }).observe(tech);
    } else restart();
  }
}

/* ─── 6 · BEFORE / AFTER ─────────────────────────────────────── */
{
  const wrap = $('#ba'), frame = wrap && $('.ba__frame', wrap), handle = $('#baHandle');
  if (frame) {
    let dragging = false;
    const set = pct => {
      pct = clamp(pct, 2, 98);
      frame.style.setProperty('--pos', pct + '%');
      handle.setAttribute('aria-valuenow', Math.round(pct));
    };
    const fromEvent = e => {
      const r = frame.getBoundingClientRect();
      set(((e.clientX - r.left) / r.width) * 100);
    };
    frame.addEventListener('pointerdown', e => { dragging = true; try { frame.setPointerCapture(e.pointerId); } catch (_) {} fromEvent(e); });
    frame.addEventListener('pointermove', e => { if (dragging) fromEvent(e); });
    frame.addEventListener('pointerup',  () => { dragging = false; });
    frame.addEventListener('pointercancel', () => { dragging = false; });
    handle.addEventListener('keydown', e => {
      const now = parseFloat(handle.getAttribute('aria-valuenow')) || 50;
      if (e.key === 'ArrowLeft')  { e.preventDefault(); set(now - 4); }
      if (e.key === 'ArrowRight') { e.preventDefault(); set(now + 4); }
    });
  }
}

/* ─── 7 · REVIEW RAIL ────────────────────────────────────────── */
{
  const rail = $('#revRail'), prog = $('#revProg');
  if (rail) {
    let down = false, startX = 0, startL = 0, moved = 0;
    rail.addEventListener('pointerdown', e => {
      if (e.pointerType === 'touch') return;          // native momentum is better
      down = true; moved = 0; startX = e.clientX; startL = rail.scrollLeft;
      rail.classList.add('drag'); try { rail.setPointerCapture(e.pointerId); } catch (_) {}
    });
    rail.addEventListener('pointermove', e => {
      if (!down) return;
      const dx = e.clientX - startX; moved = Math.abs(dx);
      rail.scrollLeft = startL - dx;
    });
    const up = () => { down = false; rail.classList.remove('drag'); };
    rail.addEventListener('pointerup', up);
    rail.addEventListener('pointercancel', up);
    rail.addEventListener('click', e => { if (moved > 6) { e.preventDefault(); e.stopPropagation(); } }, true);
    const upd = () => {
      const span = rail.scrollWidth - rail.clientWidth;
      const p = span > 0 ? rail.scrollLeft / span : 0;
      if (prog) prog.style.transform = `translateX(${p * 400}%)`;
    };
    rail.addEventListener('scroll', upd, { passive: true });
    upd();
  }
}

/* ─── 8 · BUY · GALLERY · OFFER · CART ───────────────────────── */
/* Handled by sorisse-shop.js against the real Shopify cart. */

/* ─── 9 · NEWSLETTER ─────────────────────────────────────────── */
{
  const form = $('#subForm');
  form && form.addEventListener('submit', e => {
    e.preventDefault();
    const email = $('#subEmail').value.trim();
    const msg = $('#subMsg');
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
    msg.textContent = ok ? 'Thank you — you’re on the list.' : 'Please enter a valid email address.';
    msg.style.color = ok ? 'var(--fg-2)' : 'var(--red)';
    if (ok) form.reset();
  });
}

/* ─── 9b · ANNOUNCEMENT BAR ──────────────────────────────────── */
{
  const bar = $('#ann'), x = $('#annClose');
  let dismissed = false;
  try { dismissed = localStorage.getItem('sorisse.ann') === 'off'; } catch (e) {}
  if (bar && dismissed) bar.classList.add('gone');
  x && x.addEventListener('click', () => {
    bar.classList.add('gone');
    try { localStorage.setItem('sorisse.ann', 'off'); } catch (e) {}
  });
}

/* ─── 9c · CONTACT FORM ──────────────────────────────────────── */
{
  const form = $('#cform');
  form && form.addEventListener('submit', e => {
    e.preventDefault();
    const msg = $('#cformMsg');
    const name = $('#cName').value.trim();
    const mail = $('#cEmail').value.trim();
    const body = $('#cMsg').value.trim();
    const ok = name && body && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(mail);
    if (!ok) {
      msg.textContent = 'Please add your name, a valid email and a message.';
      msg.style.color = 'var(--red)';
      return;
    }
    // No form backend yet — hand off to the mail client so nothing is lost.
    const subject = encodeURIComponent('SORISSE enquiry' + ($('#cOrder').value.trim() ? ' — order ' + $('#cOrder').value.trim() : ''));
    const text = encodeURIComponent(body + '\n\n— ' + name + '\n' + mail);
    msg.textContent = 'Opening your email app…';
    msg.style.color = 'var(--fg-2)';
    location.href = `mailto:hello@sorisse.com?subject=${subject}&body=${text}`;
  });
}

/* ─── 10 · MAGNETIC BUTTONS ──────────────────────────────────── */
if (FINE && !REDUCED) {
  $$('.mag').forEach(el => {
    let raf = null, tx = 0, ty = 0, cx = 0, cy = 0;
    const loop = () => {
      cx = lerp(cx, tx, 0.18); cy = lerp(cy, ty, 0.18);
      el.style.transform = `translate(${cx}px, ${cy}px)`;
      if (Math.abs(cx - tx) > 0.1 || Math.abs(cy - ty) > 0.1) raf = requestAnimationFrame(loop);
      else { el.style.transform = `translate(${tx}px, ${ty}px)`; raf = null; }
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(loop); };
    el.addEventListener('pointermove', e => {
      const r = el.getBoundingClientRect();
      tx = (e.clientX - (r.left + r.width / 2)) * 0.22;
      ty = (e.clientY - (r.top + r.height / 2)) * 0.34;
      kick();
    });
    el.addEventListener('pointerleave', () => { tx = ty = 0; kick(); });
  });
}

/* ─── 11 · CURSOR ────────────────────────────────────────────── */
const cursor = (() => {
  if (!FINE || REDUCED) return null;
  const el = $('#cursor'), dot = $('.cursor__dot'), ring = $('.cursor__ring');
  const p = { x: innerWidth / 2, y: innerHeight / 2, rx: innerWidth / 2, ry: innerHeight / 2 };
  addEventListener('pointermove', e => {
    p.x = e.clientX; p.y = e.clientY;
    el.classList.add('on');
  }, { passive: true });
  addEventListener('pointerdown', () => el.classList.add('hot'));
  addEventListener('pointerup',   () => el.classList.remove('hot'));
  document.addEventListener('pointerover', e => {
    const hot = e.target.closest('a, button, summary, .ba__frame, .revs__rail');
    el.classList.toggle('hot', !!hot);
  });
  return { tick() {
    p.rx = lerp(p.rx, p.x, 0.16); p.ry = lerp(p.ry, p.y, 0.16);
    dot.style.transform  = `translate(${p.x}px, ${p.y}px) translate(-50%,-50%)`;
    ring.style.transform = `translate(${p.rx}px, ${p.ry}px) translate(-50%,-50%)`;
  }};
})();

/* ─── 12 · POINTER TILT (hero) ───────────────────────────────── */
const tilt = (() => {
  const els = $$('[data-tilt]');
  if (!els.length || REDUCED || !FINE) return null;
  const t = { x: 0, y: 0, cx: 0, cy: 0 };
  addEventListener('pointermove', e => {
    t.x = (e.clientX / innerWidth  - 0.5) * 2;
    t.y = (e.clientY / innerHeight - 0.5) * 2;
  }, { passive: true });
  return { tick(scrollShift) {
    t.cx = lerp(t.cx, t.x, 0.055); t.cy = lerp(t.cy, t.y, 0.055);
    els.forEach(el => {
      const amt = parseFloat(el.dataset.tilt) || 20;
      el.style.transform =
        `translate(-50%, -46%) translate3d(${(-t.cx * amt).toFixed(2)}px, ${(-t.cy * amt * 0.7 + scrollShift).toFixed(2)}px, 0) rotate(${(t.cx * 1.6).toFixed(2)}deg)`;
    });
  }};
})();

/* ─── 13 · PACKAGING SEQUENCE ────────────────────────────────── */
const packSeq = (() => {
  const scene = $('#packScene');
  if (!scene) return null;
  const frames = $$('.pack__frames img'), caps = $$('.pack__cap p'), dots = $$('.pack__dots i');
  let last = -1;
  return { tick() {
    const r = scene.getBoundingClientRect();
    const span = r.height - innerHeight;
    if (span <= 0) return;
    const p = clamp(-r.top / span, 0, 1);
    const i = p < 0.34 ? 0 : p < 0.68 ? 1 : 2;
    if (i === last) return;
    last = i;
    frames.forEach((f, n) => f.classList.toggle('is-on', n === i));
    caps.forEach((c, n)   => c.classList.toggle('is-on', n === i));
    dots.forEach((d, n)   => d.classList.toggle('is-on', n === i));
  }};
})();

/* ─── 14 · SHOWCASE OBJECT ───────────────────────────────────── */
const showObj = (() => {
  const sec = $('#showcase'), img = sec && $('.show__obj img');
  if (!img || REDUCED) return null;
  return { tick() {
    const r = sec.getBoundingClientRect();
    const span = r.height + innerHeight;
    const p = clamp((innerHeight - r.top) / span, 0, 1);   // 0 → 1 across the section
    const s = 0.9 + Math.sin(p * Math.PI) * 0.12;
    img.style.transform = `scale(${s.toFixed(3)}) rotate(${((p - 0.5) * 9).toFixed(2)}deg)`;
  }};
})();

/* ─── 15 · PARALLAX + MASTER LOOP ────────────────────────────── */
const paras = $$('[data-speed]').map(el => ({ el, speed: parseFloat(el.dataset.speed) || 0 }));

let heroShift = 0;
function tickParallax() {
  const vh = innerHeight;
  for (const p of paras) {
    const r = p.el.getBoundingClientRect();
    if (r.bottom < -vh * 0.4 || r.top > vh * 1.4) continue;
    const off = (r.top + r.height / 2 - vh / 2) * p.speed;
    p.el.style.transform = `translate3d(0, ${off.toFixed(2)}px, 0)`;
  }
  heroShift = window.scrollY * 0.12;
}

function frame() {
  scroller.tick();
  if (!REDUCED) {
    tickParallax();
    tilt    && tilt.tick(heroShift);
    showObj && showObj.tick();
  }
  packSeq && packSeq.tick();
  cursor  && cursor.tick();
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

/* ─── 16 · NAV SCROLL STATE ──────────────────────────────────── */
{
  const ann = $('#ann');
  const upd = () => {
    const y = window.scrollY;
    nav.classList.toggle('stuck', y > 40);
    if (ann) ann.style.opacity = String(clamp(1 - y / 70, 0, 1));
  };
  addEventListener('scroll', upd, { passive: true });
  upd();
}

})();
